import { type Subscription, fromEvent, merge } from 'rxjs';

type ActionHistoryLog = {
  eventType: string;
  target: Pick<
    HTMLElement,
    'ariaLabel' | 'id' | 'role' | 'tagName' | 'textContent'
  >;
  timestamp: string;
} & Pick<KeyboardEvent, 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'> &
  Pick<MouseEvent, 'button' | 'offsetX' | 'offsetY'>;

class ActionHistory {
  static MAX_HISTORY = 10;
  static instance: ActionHistory;

  actionHistory = [] as ActionHistoryLog[];
  subscription: Nullish<Subscription>;

  constructor() {
    if (ActionHistory.instance) return ActionHistory.instance;

    ActionHistory.instance = this;
  }

  cleanup() {
    this.subscription?.unsubscribe();
    this.actionHistory = [];
    this.subscription = null;

    return this;
  }

  start() {
    this.cleanup();

    const ob$List = [
      fromEvent<KeyboardEvent>(window, 'keydown'),
      fromEvent<KeyboardEvent>(window, 'keyup'),
      fromEvent<KeyboardEvent>(window, 'keypress'),
      fromEvent<MouseEvent>(window, 'click'),
      fromEvent<MouseEvent>(window, 'mousedown'),
      fromEvent<MouseEvent>(window, 'mouseup'),
    ];

    this.subscription = merge(...ob$List).subscribe(e => {
      const target = (
        e.target instanceof SVGElement
          ? (e.target.closest('div,button,[role]') ?? (e.target as SVGElement))
          : e.target
      ) as HTMLElement | SVGElement;
      const identifier = {
        ariaLabel: target.ariaLabel,
        id: target.id,
        role: target.role,
        tagName: target.tagName,
        textContent: target.textContent,
      };
      const other = {
        altKey: e.altKey,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        key: e.type.match(/key/) ? (e as KeyboardEvent).key : '',
        offsetX: e.type.match(/mouse/) ? (e as MouseEvent).offsetX : -1,
        offsetY: e.type.match(/mouse/) ? (e as MouseEvent).offsetY : -1,
        button: e.type.match(/mouse/) ? (e as MouseEvent).button : -1,
      };

      const log = {
        eventType: e.type,
        target: identifier,
        timestamp: new Date().toISOString(),
        ...other,
      };

      this.push(log);
    });
  }

  push(log: ActionHistoryLog) {
    this.actionHistory.push(log);

    this.actionHistory.length > ActionHistory.MAX_HISTORY &&
      this.actionHistory.shift();
  }
}

export const actionHistory = new ActionHistory();
